import { INDRAPnrData, INDRAPnrDataRecon, INDRAPaymentMethodDetails } from '@ibe.models/pnr-data.indra.model.lib';
import { INDRAOutbound } from '@ibe.models/outbound.indra.model.lib';
import { INDRAInbound } from '@ibe.models/inbound.indra.model.lib';
import { INDRASegment } from '@ibe.models/segment.indra.model.lib';
import { INDRAAncillary } from '@ibe.models/ancillary.indra.model.lib';
import { SSR_CODES_CATALOG_EXCLUDED_LIST, EXTRAS_ANCILLARY_HOTEL, EXTRAS_ANCILLARY_CAR } from '@ibe.config';

export interface INDRAPnrDetailsServiceResponse {
	data: INDRAPnrData;
	discounts? : INDRADiscounts[];
	currentPoints?: number;
	externalReservations?: {
		reservationType: INDRAExternalReservation[]
	};
	status: string;
	translate: {
		airlines: INDRAAirlinesTranslations[],
		locations: INDRALocationsTranslations[]
	};
	'checkin-information': {
		flightIds: INDRACheckInInformation[];
	};
	'fare-information': {
		flightIds: INDRAFareInformation[]
	};
	'tier-information': {
		tier: INDRAPaxTierBenefits[]
	};
	fareConditionBean?: INDRAFareConditionBean;
}

export class INDRABookedTripDetails {

	data: INDRAPnrDataRecon;
	status: string;
	translate: {
		airlines: INDRAAirlinesTranslations[],
		locations: INDRALocationsTranslations[]
	};

	'checkin-information': {
		flightIds: INDRACheckInInformation[];
	};
	'fare-information': {
		flightIds: INDRAFareInformation[]
	};
	'tier-information': {
		tier: INDRAPaxTierBenefits[]
	};
	'ancillaries-data': INDRAAncillary;
	// NOTE: Custom variable incase you need to reload new data from pnr search
	reload?: boolean;
	carbon_offset?: {
		idReservation: string;
		description: string;
		currency: string;
		amount: string;
		status: string;
		bookableItemId: string;
		extraInfo: {}
	};

	danish_fund?: {
		idReservation: string;
		description: string;
		currency: string;
		amount: string;
		status: string;
		bookableItemId: string;
		extraInfo: {}
	};

	insurance?: {
		idReservation: string;
		description: string;
		currency: string;
		amount: string;
		status: string;
		bookableItemId: string;
		extraInfo: {}
	};

	hotel?: any;
	airport_transfer?: any;
	sightseeing_tour?: any;
	lisboa_card?: any;
	rented_car?: any[];
	fop?: INDRAPaymentMethodDetails;
	discounts?: INDRADiscounts[];
	externalReservations?: {
		reservationType: INDRAExternalReservation[]
	};
	currentPoints?: number;
	fareConditionBean?: INDRAFareConditionBean;

	constructor(data: INDRAPnrDetailsServiceResponse) {
		this.status = data.status;
		this.translate = data.translate;
		this['checkin-information'] = data['checkin-information'];
		this['fare-information'] = data['fare-information'];
		this['tier-information'] = data['tier-information'];
		this['ancillaries-data'] = null; // loaded async -- this.removeExcludedCatalogAncillaries(data['ancillaries-data']);
		this.externalReservations =  data.externalReservations;

		// variables that would hold the manipulated outbound/inbound flights
		const outboundFlights: INDRAOutbound[] = [];
		let inboundFlights: INDRAInbound[] = [];

		// const outboundFlights = data.data.fare.listOutbound;
		let newFlightId = 1;
		if (data.data.fare.listOutbound) {
			const outbounds: INDRAOutbound[] = data.data.fare.listOutbound.slice();
			const outboundLength = data.data.fare.listOutbound.length;
			for (let j = 0; j < outboundLength; j++) {
				if (outbounds[j].listSegment.length === 1 && !outbounds[j].listSegment[0].stopover) { // outbound flight is direct flight and not with stopover
					outbounds[j].assignedFlightId = newFlightId;
					outboundFlights.push(outbounds[j]);
					newFlightId++;
				} else {
					let tempSegmentsArr: INDRASegment[] = [];
					/*
					segments will be breaked if stopover scenarios are detected. Separate outbound flights will
					be created for stopover segments..
					*/
					const segments: INDRASegment[] = data.data.fare.listOutbound[j].listSegment.slice();
					const outboundSegmentLength = segments.length;

					for (let k = 0; k < outboundSegmentLength; k++) {
						const outbound: INDRAOutbound = { // inherit other attributes except from listSegments, idFlight
							airportChange : data.data.fare.listOutbound[j].airportChange,
							cabin : data.data.fare.listOutbound[j].cabin,
							duration : data.data.fare.listOutbound[j].duration,
							fareFamily : data.data.fare.listOutbound[j].fareFamily,
							flightFlown : data.data.fare.listOutbound[j].flightFlown,
							freeBaggageForPax : data.data.fare.listOutbound[j].freeBaggageForPax,
							idFlightAvail : data.data.fare.listOutbound[j].idFlightAvail,
							numberOfStops : data.data.fare.listOutbound[j].numberOfStops,
							totalPoints: data.data.fare.listOutbound[j].totalPoints,
							totalPrice: data.data.fare.listOutbound[j].totalPrice,
							assignedFlightId: newFlightId,
							idFlight: data.data.fare.listOutbound[j].idFlight,
							isStopOver: false,
							isLongLayover: false,
							listSegment: [],
							mixedCabins: data.data.fare.listOutbound[j].mixedCabins
						};

						if (segments[k].stopover) {
							outbound.isStopOver = true;
							if (segments[k].longLayover) {
								outbound.isLongLayover = true;
							}
							// stopover is detected. create a new outbound flight for the grouped flight segment from start index up to stop over index.
							tempSegmentsArr.push(segments[k]);
							outbound.listSegment = tempSegmentsArr.slice();
							outbound.firstSegment = outbound.listSegment[0];
							outbound.lastSegment = outbound.listSegment[outbound.listSegment.length - 1];
							outbound.flightFlown = outbound.listSegment.some(segment => segment.flightFlown);
							outboundFlights.push(outbound);
							tempSegmentsArr = [];
							newFlightId++;
						} else {
							// add the flight segment to a group until stopover is detected..
							tempSegmentsArr.push(segments[k]);
							if (k === (outboundSegmentLength - 1)) { // create a new outbound flight for the last grouped segment of the outbound..
								outbound.listSegment = tempSegmentsArr.slice();
								outbound.firstSegment = outbound.listSegment[0];
								outbound.lastSegment = outbound.listSegment[outbound.listSegment.length - 1];
								outbound.flightFlown = outbound.listSegment.some(segment => segment.flightFlown);
								outboundFlights.push(outbound);
								tempSegmentsArr = [];
								newFlightId++;
							}
						}
					}
				}
			}
		}

		if (data.data.fare.inbound) {
			const segments: INDRASegment[] = data.data.fare.inbound.listSegment.slice();
			const inboundSegmentLength = segments.length;
			if (inboundSegmentLength === 1 && !segments[0].stopover) { // inbound flight is direct flight and not with stopover
				data.data.fare.inbound.assignedFlightId = newFlightId;
				inboundFlights = [data.data.fare.inbound];
				newFlightId++;
			} else { // inbound flight has stopovers/layovers
				let tempSegmentsArr: INDRASegment[] = [];
				/* segments will be breaked if stopover scenarios are detected. Separate inbound flights will
				be created for stopover segments..
				*/
				for (let i = 0; i < inboundSegmentLength; i++) {
					const inbound: INDRAInbound = { // inherit other attributes except from listSegments, idFlight
						airportChange : data.data.fare.inbound.airportChange,
						cabin : data.data.fare.inbound.cabin,
						duration : data.data.fare.inbound.duration,
						fareFamily : data.data.fare.inbound.fareFamily,
						flightFlown : data.data.fare.inbound.flightFlown,
						freeBaggageForPax : data.data.fare.inbound.freeBaggageForPax,
						idFlightAvail : data.data.fare.inbound.idFlightAvail,
						numberOfStops : data.data.fare.inbound.numberOfStops,
						totalPoints: data.data.fare.inbound.totalPoints,
						totalPrice: data.data.fare.inbound.totalPrice,
						assignedFlightId: newFlightId,
						idFlight: data.data.fare.inbound.idFlight,
						isStopOver: false,
						isLongLayover: false,
						listSegment: [],
						mixedCabins: data.data.fare.inbound.mixedCabins
					};

					if (segments[i].stopover) {
						inbound.isStopOver = true;
						if (segments[i].longLayover) {
							inbound.isLongLayover = true;
						}
						// stopover is detected. create a new inbound flight for the grouped flight segment from start index up to stop over index.
						tempSegmentsArr.push(segments[i]);
						inbound.listSegment = tempSegmentsArr.slice();
						inbound.firstSegment = inbound.listSegment[0];
						inbound.lastSegment = inbound.listSegment[inbound.listSegment.length - 1];
						inboundFlights.push(inbound);
						tempSegmentsArr = [];
						newFlightId++;
					} else {
						// add the flight segment to a group until stopover is detected..
						tempSegmentsArr.push(segments[i]);
						if (i === (inboundSegmentLength - 1)) { // create a new inbound flight for the last grouped segment of the inbound..
							inbound.listSegment = tempSegmentsArr.slice();
							inbound.firstSegment = inbound.listSegment[0];
							inbound.lastSegment = inbound.listSegment[inbound.listSegment.length - 1];
							inboundFlights.push(inbound);
							tempSegmentsArr = [];
							newFlightId++;
						}
					}
				}
			}
		}

		this.data = {
			atcAllow : data.data.atcAllow,
			airline: data.data.airline,
			ancillaries: data.data.ancillaries,
			codeIATA: data.data.codeIATA,
			creationTime: data.data.creationTime,
			fee: data.data.fee,
			flow: data.data.flow,
			hppAncPending: data.data.hppAncPending,
			group: data.data.group,
			infoPax: data.data.infoPax,
			infoTicket: data.data.infoTicket,
			isTTT: data.data.isTTT,
			language: data.data.language,
			market: data.data.market,
			office: data.data.office,
			onHold: data.data.onHold,
			onHoldMargin: data.data.onHoldMargin,
			onHoldType: data.data.onHoldType,
			online: data.data.online,
			pnr: data.data.pnr,
			pointsOfSale: data.data.pointsOfSale,
			promoCode: data.data.promoCode,
			refundable: data.data.refundable,
			statusBook: data.data.statusBook,
			timeToThinkData: data.data.timeToThinkData,
			trn: data.data.trn,
			fare: {
				currency: data.data.fare.currency,
				flightPrice: data.data.fare.flightPrice,
				originalFlightPrice: data.data.fare.originalFlightPrice,
				tripType: data.data.fare.tripType,
				listInbound: inboundFlights,
				listOutbound: outboundFlights,
				removedPromocode: data.data.fare.removedPromocode,
				removedVoucher: data.data.fare.removedVoucher,
				bookingTotal: data.data.fare.bookingTotal,
				totalDiscount: data.data.fare.totalDiscount,
				promocodeVouchers: data.data.fare.promocodeVouchers,
				flightCost: data.data.fare.flightCost
			},
			corporate: data.data.corporate,
			originalPaxNumber: data.data.originalPaxNumber,
			pnrWaitingMbWayPayment: data.data.pnrWaitingMbWayPayment,
			pnrWaitingMultibancoPayment: data.data.pnrWaitingMultibancoPayment,
			timeLimitToPay: data.data.timeLimitToPay,
			ancillaryBasket: data.data.ancillaryBasket,
			fop: data.data.fop,
			externalReservationIds: data.data.externalReservationIds,
			award: data.data.award,
			extras: data.data.extras
		};

		this.reload = false;
		if (!this.rented_car) {
			this.rented_car = [];
		}

		if (data.discounts) {
			this.discounts = data.discounts;
		}
		
		if (data.currentPoints >= 0) {
			this.currentPoints = data.currentPoints;
		}
		
		if (data.fareConditionBean) {
			this.fareConditionBean = data.fareConditionBean;
		}
	}
}

export interface INDRAAirlinesTranslations {
	code: string;
	name: string;
}

export interface INDRALocationsTranslations {
	city: string;
	code: string;
	country: string;
	countryCode: string;
	name: string;
}

export interface INDRACheckInInformation {
	date: string;
	status: string;
}

export interface INDRAFareInformation {
	miles: number;
	actualMiles: number;
	ancillaries: string[];
	milesByFlightCard: Map<string , INDRAMilesInformation>;
	'economy-extra-elegible': boolean;
	'extra-ancillaries': string[];
	'red-and-green-elegible': boolean;
	'red-and-green-ancillaries': string[];
	ancillariesBySegment?: any;
	milesByFlightCardBySegment?: any;
}

export interface INDRAPaxTierBenefits {
	ancillaries: string[];
	milesPercentage: number;
}

export interface INDRAMilesInformation {
	flightCardId: string;
	miles: number;
	actualMiles: number;
	milePercentage: number;
}

export interface INDRADiscounts {
	paxId: number;
	amount: number;
	type: string;
	id: string;
	currency?: string;
}

export interface INDRAExternalReservation {
	idReservation: string;
	description: string;
	currency: string;
	amount: number;
	status: string;
	bookableItemId: string;
	extraInfo: any;
	stopOverInfo: any;
}

export interface INDRAFareConditionBean {
	listIdOutbound: number[];
	idInbound: number;
	idOffer: number[];
	fareFamilyOutwardCod: string;
	fareFamilyReturnCod: string;
	listRBDOutbound: string[];
	rbdInbound: string;
	forPoints: boolean;
	outStopCodes: any;
	inStopCode: any;
	ttt: boolean;
}



















